import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyState } from '@/components/EmptyState';
import { SectionTitle } from '@/components/SectionTitle';
import { LinkRevealDialog, type RevealedLink } from '@/components/LinkRevealDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAdminInvites, useCancelInvite, useCreateInvite, useRegenerateInviteLink } from '@/hooks/use-invites';
import { useAdminProjects } from '@/hooks/use-projects';
import { ApiClientError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import type { AdminInvite, GrantRole } from '@/types/invites';

/** Sprint 12 (§7.3): at most 20 project grants per invite. */
export const MAX_PROJECT_GRANTS = 20;
const GRANT_ROLE_LABEL: Record<GrantRole, string> = { member: 'Member', supervisor: 'Supervisor' };

interface GrantDraft {
  key: number;
  projectId: string;
  role: GrantRole;
}

/** "Add to projects": optional rows of an active project plus the role the invitee gets there. */
function ProjectGrantRows({ rows, onChange }: { rows: GrantDraft[]; onChange: (rows: GrantDraft[]) => void }): JSX.Element {
  const projects = useAdminProjects('active');
  const choices = projects.data ?? [];
  const nextKey = rows.reduce((max, r) => Math.max(max, r.key), 0) + 1;
  const update = (key: number, patch: Partial<GrantDraft>): void => onChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">Add to projects (optional)</p>
      {rows.map((row, i) => (
        <div key={row.key} className="grid gap-2 sm:grid-cols-[1fr_10rem_auto]">
          <NativeSelect aria-label={`Project ${i + 1}`} value={row.projectId} onChange={(e) => update(row.key, { projectId: e.target.value })}>
            <option value="">Choose a project</option>
            {choices
              .filter((p) => p.id === row.projectId || !rows.some((r) => r.projectId === p.id))
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </NativeSelect>
          <NativeSelect
            aria-label={`Project role ${i + 1}`}
            value={row.role}
            onChange={(e) => update(row.key, { role: e.target.value === 'supervisor' ? 'supervisor' : 'member' })}
          >
            <option value="member">Member</option>
            <option value="supervisor">Supervisor</option>
          </NativeSelect>
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange(rows.filter((r) => r.key !== row.key))}>
            Remove
          </Button>
        </div>
      ))}
      {rows.length < MAX_PROJECT_GRANTS && (
        <Button type="button" variant="ghost" size="xs" onClick={() => onChange([...rows, { key: nextKey, projectId: '', role: 'member' }])}>
          Add to a project
        </Button>
      )}
    </div>
  );
}

const createSchema = z.object({
  email: z.string().trim().min(1, 'Enter an email address').email('Enter a valid email address').max(255),
  role: z.enum(['user', 'admin']),
});
type CreateValues = z.infer<typeof createSchema>;

/**
 * The invite texts of the reveal dialog, unchanged since sprint 2 (the dialog was generalised in sprint 5).
 * Sprint 12: also used by the project Members tab (Add by email, New link).
 */
export function inviteReveal(link: string, email: string, expiresAt: string): RevealedLink {
  return {
    link,
    title: 'Invite link',
    inputLabel: 'Invite link',
    description: (
      <>
        This link is shown only once and expires on {formatDateTime(expiresAt)}. Share it with {email} yourself; Clockit doesn&apos;t send
        email.
      </>
    ),
  };
}

function CreateInviteForm({ onCreated }: { onCreated: (link: RevealedLink) => void }): JSX.Element {
  const createInvite = useCreateInvite();
  const [serverError, setServerError] = useState<string | null>(null);
  const [grants, setGrants] = useState<GrantDraft[]>([]);
  const form = useForm<CreateValues>({ resolver: zodResolver(createSchema), defaultValues: { email: '', role: 'user' } });

  function onSubmit(values: CreateValues): void {
    setServerError(null);
    // Rows without a project are ignored, so an empty row never blocks the invite.
    const projectGrants = grants.filter((g) => g.projectId !== '').map((g) => ({ projectId: g.projectId, role: g.role }));
    createInvite.mutate(
      { email: values.email.trim().toLowerCase(), role: values.role, ...(projectGrants.length > 0 ? { projectGrants } : {}) },
      {
        onSuccess: ({ invite, link }) => {
          form.reset({ email: '', role: values.role });
          setGrants([]);
          onCreated(inviteReveal(link, invite.email, invite.expiresAt));
        },
        onError: (err) => setServerError(err instanceof ApiClientError ? err.message : 'Something went wrong. Please try again.'),
      },
    );
  }

  const emailError = form.formState.errors.email?.message;
  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="card space-y-3" noValidate>
      <div className="grid gap-3 sm:grid-cols-[1fr_10rem_auto] sm:items-end">
        <div className="grid gap-1.5">
          <Label htmlFor="invite-email">Email</Label>
          <Input id="invite-email" type="email" autoComplete="off" placeholder="name@skaleminds.com" {...form.register('email')} aria-invalid={emailError ? true : undefined} />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="invite-role">Role</Label>
          <NativeSelect id="invite-role" {...form.register('role')}>
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </NativeSelect>
        </div>
        <Button type="submit" disabled={createInvite.isPending}>
          Create invite
        </Button>
      </div>
      <ProjectGrantRows rows={grants} onChange={setGrants} />
      {emailError && <p className="text-xs text-danger-fg">{emailError}</p>}
      {serverError && (
        <p role="alert" className="text-sm text-danger-fg">
          {serverError}
        </p>
      )}
    </form>
  );
}

function InviteRow({ invite, onNewLink, onCancel }: { invite: AdminInvite; onNewLink: () => void; onCancel: () => void }): JSX.Element {
  const regenerated = invite.linkGeneratedBy.id !== invite.invitedBy.id;
  return (
    <TableRow>
      <TableCell className="font-medium">{invite.email}</TableCell>
      <TableCell>
        <Badge variant="outline">{invite.role === 'admin' ? 'Admin' : 'User'}</Badge>
      </TableCell>
      <TableCell>
        {invite.projectGrants.length === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <ul className="space-y-0.5 text-sm">
            {invite.projectGrants.map((g) => (
              <li key={g.id}>
                {g.project.name} · {GRANT_ROLE_LABEL[g.role]}
              </li>
            ))}
          </ul>
        )}
      </TableCell>
      <TableCell>{invite.invitedBy.name}</TableCell>
      <TableCell className="font-mono tabular-nums">
        {formatDateTime(invite.linkGeneratedAt)}
        {regenerated && <span className="block font-sans text-xs text-muted-foreground">by {invite.linkGeneratedBy.name}</span>}
      </TableCell>
      <TableCell className="font-mono tabular-nums">
        <span className="inline-flex flex-wrap items-center gap-1.5">
          {formatDateTime(invite.expiresAt)}
          {invite.expired && <Badge variant="danger">Expired</Badge>}
        </span>
      </TableCell>
      <TableCell>
        <div className="flex justify-end gap-2">
          <Button size="xs" variant="outline" onClick={onNewLink}>
            New link
          </Button>
          <Button size="xs" variant="ghost" onClick={onCancel}>
            Cancel invite
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export function InvitesTab(): JSX.Element {
  const { data: invites, isLoading } = useAdminInvites();
  const regenerate = useRegenerateInviteLink();
  const cancel = useCancelInvite();
  const [revealed, setRevealed] = useState<RevealedLink | null>(null);
  const [confirmNewLink, setConfirmNewLink] = useState<AdminInvite | null>(null);
  const [confirmCancel, setConfirmCancel] = useState<AdminInvite | null>(null);

  return (
    <div>
      <section className="mb-8">
        <SectionTitle title="Invite someone" />
        <CreateInviteForm onCreated={setRevealed} />
      </section>
      <SectionTitle title="Pending invites" />

      {isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : !invites || invites.length === 0 ? (
        <EmptyState title="No pending invites." />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Projects</TableHead>
              <TableHead>Invited by</TableHead>
              <TableHead>Generated</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invites.map((invite) => (
              <InviteRow key={invite.id} invite={invite} onNewLink={() => setConfirmNewLink(invite)} onCancel={() => setConfirmCancel(invite)} />
            ))}
          </TableBody>
        </Table>
      )}

      <ConfirmDialog
        open={confirmNewLink !== null}
        onOpenChange={(o) => !o && setConfirmNewLink(null)}
        title="Generate a new link?"
        body="The current link stops working."
        confirmLabel="Generate link"
        pending={regenerate.isPending}
        onConfirm={() => {
          if (!confirmNewLink) return;
          regenerate.mutate(confirmNewLink.id, {
            onSuccess: ({ invite, link }) => {
              setConfirmNewLink(null);
              setRevealed(inviteReveal(link, invite.email, invite.expiresAt));
            },
            onError: () => setConfirmNewLink(null),
          });
        }}
      />
      <ConfirmDialog
        open={confirmCancel !== null}
        onOpenChange={(o) => !o && setConfirmCancel(null)}
        title="Cancel this invite?"
        body={confirmCancel ? `The link for ${confirmCancel.email} stops working.` : ''}
        confirmLabel="Cancel invite"
        destructive
        pending={cancel.isPending}
        onConfirm={() => {
          if (!confirmCancel) return;
          cancel.mutate(confirmCancel.id, {
            onSuccess: () => {
              toast.success('Invite cancelled.');
              setConfirmCancel(null);
            },
            onError: () => setConfirmCancel(null),
          });
        }}
      />
      {/* Closing drops the link from state: it is never shown again (§10.2). */}
      <LinkRevealDialog value={revealed} onClose={() => setRevealed(null)} />
    </div>
  );
}
